import { useMemo, useState } from 'react'
import * as actions from '@actions'
import * as store from '@store'
import { useFormik } from 'formik'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import { Message } from '@components/shared/Message'
import MetaRow from '@components/tract/UpsertDocument/MetaRow'
import GrantorGranteeRow from '@components/tract/UpsertDocument/GrantorGranteeRow'
import { toast } from 'react-toastify'
// import { TextInput } from '@/components/shared/TextInput'
import { parseMoment } from '@/utils/date'
import { handlePartyChanges } from '../Runsheet/Runsheet.lib'
import { useWireState } from '@forminator/react-wire'
import { ModalBody, ModalFooter, ModalHeader } from '@/components/shared/CustomModal'
import { buildRecordUrl } from '@/utils'
import ScrollableDocumentViewer from '@/components/collection/DocumentViewerV3/ScrollableDocumentViewer'
import { Link } from 'react-router-dom'
import { MdEditNote } from 'react-icons/md'

/* type UpsertDocumentProps = {
    runsheetId: string
    showAdd: boolean
    setShowAdd: (showAdd: boolean) => void
    editRsDocJoin: object
} */

export const UpsertDocument = ({
    runsheetId,
    showAdd,
    setShowAdd,
    editRsDocJoin,
    onSuccess,
}) => {
    
    const [message, setMessage] = useState()
    const [grantors, setGrantors] = useState(editRsDocJoin?.value?.grantors
        ? editRsDocJoin.value.grantors.map(g => ({ id: g.id, name: g.name })) : [])
    const [grantees, setGrantees] = useState(editRsDocJoin?.value?.grantees
        ? editRsDocJoin.value.grantees.map(g => ({ id: g.id, name: g.name })) : [])
    // const [fullGrantor, setFullGrantor] = useState(editRsDocJoin?.value.fullGrantor)
    // const [fullGrantee, setFullGrantee] = useState(editRsDocJoin?.value.fullGrantee)
    const [instrumentDate, setInstrumentDate] = useState(parseMoment(editRsDocJoin?.value.instrumentDate))
    const [fileDate, setFileDate] = useState(parseMoment(editRsDocJoin?.value.fileDate))
    const [, setRunsheetDocumentValue] = useWireState(store.runsheetDocumentValue)
    
    const document = useMemo(() => editRsDocJoin?.document || {}, [editRsDocJoin])
    
    const pdfUrl = useMemo(() => buildRecordUrl(document, true, document.collection?.isParse), [document])
    
    const buildDoc = ({
        bookType,
        bookPage,
        bookVolume,
        instrumentNumber,
        instrumentType,
        fullGrantor,
        fullGrantee,
        comments,
        notes,
    }) => {
        return {
            runsheetId,
            grantors: handlePartyChanges(grantors, editRsDocJoin?.value?.grantors || []),
            grantees: handlePartyChanges(grantees, editRsDocJoin?.value?.grantees || []),
            fullGrantor,
            fullGrantee,
            bookPage: bookPage || '',
            bookType: bookType || '',
            instrumentDate: instrumentDate,
            instrumentNumber: instrumentNumber || '',
            instrumentType: instrumentType || '',
            bookVolume: bookVolume || '',
            fileDate: fileDate,
            comments: comments || '',
            notes: notes || '',
        }
    }
    
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            id: editRsDocJoin?.id || '',
            bookType: editRsDocJoin?.value.bookType || '',
            bookPage: editRsDocJoin?.value.bookPage || '',
            bookVolume: editRsDocJoin?.value.bookVolume || '',
            instrumentNumber: editRsDocJoin?.value.instrumentNumber || '',
            instrumentType: editRsDocJoin?.value.instrumentType || '',
            comments: editRsDocJoin?.value.comments || '',
            notes: editRsDocJoin?.value.notes || '',
            fullGrantor: editRsDocJoin?.value.fullGrantor || '',
            fullGrantee: editRsDocJoin?.value.fullGrantee || '',
        },
        onSubmit: async value => {
            if (!validateRunsheet()) {
                setMessage({ text: 'Please fill in all the required fields', level: 'danger' })
                return
            }
            
            const upsertedDocumentObj = buildDoc(value)
            
            upsertDocument(upsertedDocumentObj)
        },
    })
    
    const { handleSubmit, handleChange, values, errors } = formik
    
    const handleModal = () => {
        setShowAdd(!showAdd)
    }
    
    const handleGrantorGranteeChange = (idx, state, setter) => e => {
        let existingData = [...state]
        const fieldName = e.target.name
        
        // prevents numbers for name column - @todo check if this is needed
        if (fieldName === 'name' && Number(e.target.value) > 1) {
            e.target.value = ' '
        }
        
        if (!existingData[idx]) {
            existingData[idx] = {}
        }
        
        let grantor = existingData[idx]
        
        existingData[idx] = { ...grantor, [fieldName]: e.target.value }
        setter(existingData)
    }
    
    const handlePartiesRowDelete = (state, setter) => index => {
        state.splice(index, 1)
        setter([...state])
    }
    
    const handlePartyRowAdd = (state, setter) => () => setter([...state, {}])
    
    const validateRunsheet = () => {
        return true
    }
    
    const dateFormatter = date => date && new Date(date).toISOString().split('T')[0] || null
    
    const upsertDocument = async (doc, redirect = true) => {
        
        if (editRsDocJoin && editRsDocJoin.document.id !== '') {
            doc.instrumentDate = dateFormatter(doc.instrumentDate)
            doc.fileDate = dateFormatter(doc.fileDate)
            await actions.updateDocument(editRsDocJoin.document.id, doc)
            onSuccess?.()
            
            if (redirect) {
                setShowAdd(!showAdd)
                toast.success('Document successfully updated')
            }
        }
        
    }
    
    return (
        <CustomModal className="UpsertDocument max-w-[90vw]" open={showAdd} modalHandler={() => handleModal()}>
            <form onSubmit={handleSubmit}>
                <ModalHeader>
                    Edit document details for <i>{editRsDocJoin?.document?.name || ''}</i>
                </ModalHeader>
                
                <ModalBody>
                    <div className="flex gap-4">
                        <div className="flex-1 relative h-[70vh]">
                            <ScrollableDocumentViewer url={pdfUrl} />
                        </div>
                        <div className="flex-1 max-h-[70vh] overflow-auto pr-2 -mr-2">
                            {message && <Message message={message} />}
                            
                            <MetaRow
                                errors={errors}
                                values={values}
                                onChange={handleChange}
                                instrumentDate={instrumentDate}
                                setInstrumentDate={setInstrumentDate}
                                fileDate={fileDate}
                                setFileDate={setFileDate}
                                legalDescriptions={editRsDocJoin?.value?.legalDescriptions}
                                showLegal={async () => {
                                    const doc = buildDoc(values)
                                    
                                    upsertDocument(doc, false)
                                    setRunsheetDocumentValue({
                                        ...editRsDocJoin.value,
                                        ...doc,
                                        clickOrigin: 'edit-modal',
                                    })
                                    setShowAdd(false)
                                }} />
                            
                            {/* <div className="mt-4 mb-2">
                                <TextInput
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="How the Grantor(s) should appear on a runsheet."
                                    name="fullGrantor"
                                    className="inline-party-field"
                                    inputClassName="w-full input input-bordered"
                                    value={fullGrantor ?? ''}
                                    handleChange={evt => setFullGrantor(evt.target.value)}
                                    label="Full Grantor" />
                            </div> */}
                            <div className="flex flex-col gap-2">
                                <label className="flex justify-between">Grantor(s)
                                    <button
                                        className="btn btn-xs btn-link"
                                        type="button"
                                        onClick={handlePartyRowAdd(grantors, setGrantors)}>
                                        Add Grantor
                                    </button>
                                </label>
                                {grantors.map((grantor, i) => (
                                    <GrantorGranteeRow
                                        key={`GrantorRow-${i}`}
                                        index={i}
                                        data={grantor}
                                        handleChange={handleGrantorGranteeChange(i, grantors, setGrantors)}
                                        handleRowDelete={handlePartiesRowDelete(grantors, setGrantors)} />
                                ))}
                            </div>
                            
                            {/* <button
                                className="mt-3 btn btn-sm btn-secondary"
                                type="button"
                                onClick={handlePartyRowAdd(grantors, setGrantors)}>
                                Add Grantor
                            </button> */}
                            
                            {/* <div className="mt-4 mb-2">
                                <TextInput
                                    data-tooltip-id="default-tip"
                                    data-tooltip-content="How the Grantee(s) should appear on a runsheet."
                                    name="fullGrantee"
                                    className="col inline-party-field"
                                    inputClassName="w-full input input-bordered"
                                    value={fullGrantee ?? ''}
                                    handleChange={evt => setFullGrantee(evt.target.value)}
                                    label="Full Grantee" />
                            </div> */}
                            
                            <div className="flex flex-col gap-2 mt-3">
                                <label className="flex justify-between">Grantee(s)
                                    <button
                                        className="btn btn-xs btn-link"
                                        type="button"
                                        onClick={handlePartyRowAdd(grantees, setGrantees)}>
                                        Add Grantee
                                    </button>
                                </label>
                                {grantees.map((grantee, i) => (
                                    <GrantorGranteeRow
                                        key={`GranteeRow-${i}`}
                                        index={i}
                                        data={grantee}
                                        handleChange={handleGrantorGranteeChange(i, grantees, setGrantees)}
                                        handleRowDelete={handlePartiesRowDelete(grantees, setGrantees)} />
                                ))}
                            </div>
                            
                            {/* <button
                                className="mt-3 btn btn-sm btn-secondary"
                                type="button"
                                onClick={handlePartyRowAdd(grantees, setGrantees)}>
                                Add Grantee
                            </button> */}
                        
                        </div>
                    </div>
                </ModalBody>
                
                <ModalFooter>
                    {editRsDocJoin && <Link className="btn btn-secondary" to={`/edit/${editRsDocJoin.document.id}`}>
                        <MdEditNote className="text-xl" />Edit Document
                    </Link>}
                    <button type="button" className="btn btn-primary btn-outline" onClick={() => handleModal()}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Save</button>
                </ModalFooter>
            </form>
        </CustomModal>
    )
}
